import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
    FiSave, FiX, FiPlus, FiTrash2,
    FiChevronRight, FiChevronDown,
} from "react-icons/fi";
import { useApp } from "../../context/AppContext";
import timetableService from "../../services/timetableService";
import courseService from "../../services/courseService";
import { getYearOptions, getSemesterOptions } from "../../utils/courseYears";
import subjectService from "../../services/subjectService";


// ─── Constants ────────────────────────────────────────────────────────────────
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const PERIOD_TYPES = ["Lecture", "Practical", "Tutorial", "Break", "Free"];

const TYPE_COLORS = {
    Lecture: { bg: "#e8f1f8", color: "#002747" },
    Practical: { bg: "#dcfce7", color: "#15803d" },
    Tutorial: { bg: "#fef9c3", color: "#92400e" },
    Break: { bg: "#f3f4f6", color: "#6b7280" },
    Free: { bg: "#f3f4f6", color: "#9ca3af" },
};

// Default period slots — admin can modify these
const DEFAULT_SLOTS = [
    { periodNo: 1, startTime: "09:30", endTime: "10:30", label: "" },
    { periodNo: 2, startTime: "10:30", endTime: "11:30", label: "" },
    { periodNo: 3, startTime: "11:30", endTime: "12:30", label: "" },
    { periodNo: 4, startTime: "12:30", endTime: "13:00", label: "Recess" },
    { periodNo: 5, startTime: "13:00", endTime: "14:00", label: "" },
    { periodNo: 6, startTime: "14:00", endTime: "15:00", label: "" },
    { periodNo: 7, startTime: "15:00", endTime: "16:00", label: "" },
    { periodNo: 8, startTime: "16:00", endTime: "17:00", label: "" },
];

const emptyCell = (periodNo) => ({
    periodNo, startTime: "", endTime: "",
    subject: null, subjectName: "", teacher: "", room: "", type: "Lecture",
});

const buildEmptySchedule = (slots) =>
    DAYS.map((day) => ({
        day,
        periods: slots.map((s) => ({
            periodNo: s.periodNo,
            startTime: s.startTime,
            endTime: s.endTime,
            subject: null,
            subjectName: "",
            teacher: "",
            room: "",
            type: "Lecture",
        })),
    }));

const normalizeTimetable = (payload) => payload?.data || payload || null;

// ─── Cell editor modal ────────────────────────────────────────────────────────
function CellModal({ cell, slotLabel, day, subjects = [], onSave, onClose }) {
    const [form, setForm] = useState({ ...cell });
    const [useCustomSubject, setUseCustomSubject] = useState(
        subjects.length === 0 || (Boolean(cell.subjectName) && !cell.subject)
    );

    const getTeacherNameFromSubject = (subj) => {
        if (!subj) return "";
        if (Array.isArray(subj.teachers) && subj.teachers.length > 0) {
            return subj.teachers
                .map((t) => (typeof t === "object" ? t?.fullName || t?.name || "" : t))
                .filter(Boolean)
                .join(", ");
        }
        if (typeof subj.teacher === "string") return subj.teacher;
        return "";
    };

    const handlePickSubject = (subjectId) => {
        if (subjectId === "__other__") {
            setUseCustomSubject(true);
            setForm((p) => ({ ...p, subject: null, subjectName: "" }));
            return;
        }
        const picked = subjects.find((s) => s._id === subjectId);
        const autoTeacher = getTeacherNameFromSubject(picked);

        setForm((p) => ({
            ...p,
            subject: subjectId || null,
            subjectName: picked?.name || "",
            teacher: autoTeacher,
            ...(picked?.type === "Practical" ? { type: "Practical" } : {}),
        }));
    };

    const currentSubjectDoc = subjects.find((s) => s._id === form.subject);
    const assignedTeachers = Array.isArray(currentSubjectDoc?.teachers)
        ? currentSubjectDoc.teachers.filter((t) => t && (typeof t === "object" ? t.fullName : t))
        : [];

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal" style={{ width: 380 }} onClick={(e) => e.stopPropagation()}>
                <h3 style={{ marginBottom: 4 }}>
                    {day} — Period {cell.periodNo}
                </h3>
                <p style={{ fontSize: 12.5, color: "var(--text-muted)", marginBottom: "1rem" }}>
                    {cell.startTime} – {cell.endTime}
                    {slotLabel ? ` · ${slotLabel}` : ""}
                </p>

                <div style={{ display: "grid", gap: "0.75rem" }}>
                    {/* Type */}
                    <div className="form-group">
                        <label className="form-label">Type</label>
                        <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                            {PERIOD_TYPES.map((t) => {
                                const active = form.type === t;
                                const c = TYPE_COLORS[t];
                                return (
                                    <button
                                        key={t}
                                        type="button"
                                        onClick={() => setForm((p) => ({ ...p, type: t }))}
                                        style={{
                                            padding: "4px 12px", borderRadius: 99, fontSize: 12,
                                            fontWeight: 600, border: "none", cursor: "pointer",
                                            background: active ? c.bg : "#f3f4f6",
                                            color: active ? c.color : "#6b7280",
                                            outline: active ? `2px solid ${c.color}` : "none",
                                            outlineOffset: 1,
                                        }}
                                    >
                                        {t}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Subject */}
                    <div className="form-group">
                        <label className="form-label">Subject</label>
                        {useCustomSubject ? (
                            <div style={{ display: "flex", gap: "0.4rem" }}>
                                <input
                                    className="form-control"
                                    value={form.subjectName}
                                    onChange={(e) => setForm((p) => ({ ...p, subject: null, subjectName: e.target.value }))}
                                    placeholder="e.g. Pharmaceutics"
                                    autoFocus
                                />
                                {subjects.length > 0 && (
                                    <button type="button" className="btn btn-outline btn-sm" onClick={() => setUseCustomSubject(false)}>
                                        Choose from list
                                    </button>
                                )}
                            </div>
                        ) : (
                            <select
                                className="form-control"
                                value={form.subject || ""}
                                onChange={(e) => handlePickSubject(e.target.value)}
                                autoFocus
                            >
                                <option value="">Select subject…</option>
                                {subjects.map((s) => (
                                    <option key={s._id} value={s._id}>{s.name}</option>
                                ))}
                                <option value="__other__">Other (type manually)…</option>
                            </select>
                        )}
                        {subjects.length === 0 && (
                            <span className="form-hint">
                                No subjects found in master for this course/year — add them under Subjects, or type manually above.
                            </span>
                        )}
                    </div>

                    {/* Teacher */}
                    <div className="form-group">
                        <label className="form-label">Teacher</label>
                        <input
                            className="form-control"
                            value={form.teacher}
                            onChange={(e) => setForm((p) => ({ ...p, teacher: e.target.value }))}
                            placeholder="e.g. Dr. Sharma"
                        />
                        {assignedTeachers.length > 1 && (
                            <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap", marginTop: "0.35rem" }}>
                                <span style={{ fontSize: 11, color: "var(--text-muted)", alignSelf: "center" }}>Assigned:</span>
                                {assignedTeachers.map((t) => {
                                    const name = typeof t === "object" ? t.fullName : t;
                                    const isCurrent = form.teacher === name;
                                    return (
                                        <button
                                            key={typeof t === "object" ? t._id : t}
                                            type="button"
                                            className="btn btn-outline btn-sm"
                                            style={{
                                                padding: "2px 8px",
                                                fontSize: 11,
                                                borderRadius: 99,
                                                background: isCurrent ? "#e0e7ff" : "transparent",
                                                borderColor: isCurrent ? "#6366f1" : "var(--border)",
                                                color: isCurrent ? "#4338ca" : "inherit",
                                            }}
                                            onClick={() => setForm((p) => ({ ...p, teacher: name }))}
                                        >
                                            {name}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Room */}
                    <div className="form-group">
                        <label className="form-label">Room / Lab</label>
                        <input
                            className="form-control"
                            value={form.room}
                            onChange={(e) => setForm((p) => ({ ...p, room: e.target.value }))}
                            placeholder="e.g. Lab 2 / Room 301"
                        />
                    </div>
                </div>

                <div className="modal-actions" style={{ marginTop: "1.25rem" }}>
                    <button className="btn btn-outline" onClick={onClose}>Cancel</button>
                    <button
                        className="btn btn-outline btn-sm"
                        style={{ color: "#dc2626", borderColor: "#dc2626" }}
                        onClick={() => {
                            onSave({ ...form, subject: null, subjectName: "", teacher: "", room: "", type: "Free" });
                        }}
                    >
                        Clear Cell
                    </button>
                    <button className="btn btn-primary" onClick={() => onSave(form)}>
                        <FiSave size={13} /> Save
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─── Main editor ──────────────────────────────────────────────────────────────
export default function TimetableEditor() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { academicYear } = useApp();
    const isEdit = Boolean(id);

    // Form state
    const [courseId, setCourseId] = useState(searchParams.get("course") || "");
    const [yearNumber, setYearNumber] = useState(Number(searchParams.get("year")) || "");
    const [semesterNumber, setSemesterNumber] = useState(Number(searchParams.get("semesterNumber")) || "");
    const [courses, setCourses] = useState([]);
    const [masterSubjects, setMasterSubjects] = useState([]);
    const [effectiveFrom, setEffectiveFrom] = useState("");
    const [notes, setNotes] = useState("");
    const [slots, setSlots] = useState(DEFAULT_SLOTS);
    const [schedule, setSchedule] = useState(() => buildEmptySchedule(DEFAULT_SLOTS));

    // UI state
    const [activeDay, setActiveDay] = useState("Monday");
    const [editingCell, setEditingCell] = useState(null); // { dayIdx, periodIdx }
    const [slotsExpanded, setSlotsExpanded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(isEdit);

    useEffect(() => {
        courseService.getAll({ isActive: true })
            .then((res) => setCourses(res.data || []))
            .catch((err) => toast.error("Couldn't load courses: " + err.message));
    }, []);

    useEffect(() => {
        if (!courseId || !yearNumber) {
            setMasterSubjects([]);
            return;
        }

        const selectedCourse = courses.find(
            (c) => String(c._id) === String(courseId)
        );

        if (!selectedCourse) {
            setMasterSubjects([]);
            return;
        }

        const yearOptions = getYearOptions(selectedCourse);

        const selectedYear = yearOptions.find(
            (y) => Number(y.yearNumber) === Number(yearNumber)
        );

        if (!selectedYear) {
            setMasterSubjects([]);
            return;
        }

        const params = {
            course: courseId,
            yearNumber: Number(yearNumber),
            isActive: true,
        };
        if (semesterNumber) {
            params.semesterNumber = Number(semesterNumber);
        }

        subjectService
            .getAll(params)
            .then((res) => {
                const list = Array.isArray(res)
                    ? res
                    : Array.isArray(res?.data)
                        ? res.data
                        : [];

                setMasterSubjects(list);
            })
            .catch(() => setMasterSubjects([]));
    }, [courseId, yearNumber, semesterNumber, courses]);

    const selectedCourse = courses.find(
        (c) => String(c._id) === String(courseId)
    );

    const yearOptions = selectedCourse
        ? getYearOptions(selectedCourse)
        : [];

    const semesterOptions = selectedCourse
        ? getSemesterOptions(selectedCourse, yearNumber)
        : [];

    const selectedYearOption = yearOptions.find(
        (y) => Number(y.yearNumber) === Number(yearNumber)
    );

    // ── Load existing timetable in edit mode ────────────────────────────────
    useEffect(() => {
        if (!isEdit) return;

        setFetching(true);

        timetableService
            .getById(id)
            .then((res) => {
                const d = normalizeTimetable(res);

                if (!d) {
                    throw new Error("Timetable not found.");
                }

                const existingSlots = Array.isArray(d.periodSlots)
                    ? d.periodSlots
                    : DEFAULT_SLOTS;

                const existingSchedule = Array.isArray(d.schedule)
                    ? d.schedule.map((day) => ({
                        ...day,
                        periods: day.periods.map((p) => ({
                            ...p,
                            subject: p.subject?._id || p.subject || null,
                        })),
                    }))
                    : [];

                const existingCourseId =
                    typeof d.course === "object"
                        ? d.course?._id
                        : d.course;

                setCourseId(existingCourseId || "");
                setYearNumber(Number(d.yearNumber) || "");
                setSemesterNumber(d.semesterNumber ? Number(d.semesterNumber) : "");

                setEffectiveFrom(d.effectiveFrom || "");
                setNotes(d.notes || "");

                setSlots(
                    existingSlots.length > 0
                        ? existingSlots
                        : DEFAULT_SLOTS
                );

                setSchedule(
                    existingSchedule.length > 0
                        ? existingSchedule
                        : buildEmptySchedule(existingSlots)
                );
            })
            .catch((err) => {
                toast.error(err.message);
                navigate("/timetables");
            })
            .finally(() => setFetching(false));
    }, [id, isEdit, navigate]);

    // ── Rebuild schedule when slots change (new timetable only) ─────────────
    const handleSlotsChange = (newSlots) => {
        setSlots(newSlots);
        // Only auto-rebuild in create mode; in edit keep existing cell values
        if (!isEdit) {
            setSchedule(buildEmptySchedule(newSlots));
        }
    };

    // ── Slot CRUD ────────────────────────────────────────────────────────────
    const addSlot = () => {
        const next = slots.length + 1;
        const newSlots = [...slots, { periodNo: next, startTime: "", endTime: "", label: "" }];
        handleSlotsChange(newSlots);
    };

    const removeSlot = (idx) => {
        if (slots.length === 1) { toast.error("At least one slot required."); return; }
        const newSlots = slots.filter((_, i) => i !== idx)
            .map((s, i) => ({ ...s, periodNo: i + 1 }));
        handleSlotsChange(newSlots);
    };

    const updateSlot = (idx, field, value) => {
        const newSlots = slots.map((s, i) => i === idx ? { ...s, [field]: value } : s);
        handleSlotsChange(newSlots);
    };

    // ── Cell editing ─────────────────────────────────────────────────────────
    const openCell = (dayIdx, periodIdx) => setEditingCell({ dayIdx, periodIdx });

    const saveCell = (updatedCell) => {
        const { dayIdx, periodIdx } = editingCell;
        setSchedule((prev) => prev.map((dayObj, di) => {
            if (di !== dayIdx) return dayObj;
            return {
                ...dayObj,
                periods: dayObj.periods.map((p, pi) =>
                    pi === periodIdx ? { ...p, ...updatedCell } : p
                ),
            };
        }));
        setEditingCell(null);
    };

    // ── Copy day ──────────────────────────────────────────────────────────────
    const copyDayTo = (fromDay, toDay) => {
        const fromSchedule = schedule.find((d) => d.day === fromDay);
        if (!fromSchedule) return;
        setSchedule((prev) => prev.map((d) =>
            d.day === toDay ? { ...d, periods: fromSchedule.periods.map((p) => ({ ...p })) } : d
        ));
        toast.success(`Copied ${fromDay} → ${toDay}`);
    };

    // ── Submit ────────────────────────────────────────────────────────────────
    const handleSubmit = async () => {
        if (!academicYear?._id) {
            toast.error("Select an academic year from the Dashboard.");
            return;
        }

        if (!courseId) {
            toast.error("Select a course.");
            return;
        }

        if (!yearNumber) {
            toast.error("Select a course year.");
            return;
        }

        const numericYear = Number(yearNumber);

        if (!Number.isInteger(numericYear) || numericYear < 1) {
            toast.error("Invalid course year.");
            return;
        }

        if (!selectedCourse) {
            toast.error("Selected course could not be found.");
            return;
        }

        if (!selectedYearOption) {
            toast.error("Selected year is not valid for this course.");
            return;
        }

        const badSlot = slots.find(
            (s) => !s.startTime || !s.endTime
        );

        if (badSlot) {
            toast.error(
                `Period ${badSlot.periodNo} is missing start or end time.`
            );
            return;
        }

        setLoading(true);

        try {
            const payload = {
                academicYear: academicYear._id,
                course: courseId,
                yearNumber: numericYear,
                semesterNumber: semesterNumber ? Number(semesterNumber) : null,
                effectiveFrom,
                notes,
                periodSlots: slots,
                schedule,
            };

            if (isEdit) {
                const res = await timetableService.update(id, payload);

                toast.success(res.message);

                navigate(`/timetables/${id}`);
            } else {
                const res = await timetableService.create(payload);

                toast.success(res.message);

                const created = normalizeTimetable(res);

                navigate(
                    created?._id
                        ? `/timetables/${created._id}`
                        : "/timetables"
                );
            }
        } catch (err) {
            toast.error(
                err.message || "Failed to save timetable."
            );
        } finally {
            setLoading(false);
        }
    };

    const activeDayIdx = DAYS.indexOf(activeDay);
    const activeDaySchedule = schedule[activeDayIdx] || { day: activeDay, periods: [] };

    if (fetching) return <div className="loading-center"><div className="spinner" />Loading timetable...</div>;

    return (
        <div>
            {/* ── Header ── */}
            <div className="page-header" style={{ flexWrap: "wrap", gap: "0.5rem" }}>
                <div>
                    <h1 className="page-title">{isEdit ? "Edit Timetable" : "Create Timetable"}</h1>
                    <p className="page-subtitle">
                        {selectedCourse && selectedYearOption
                            ? `${selectedCourse.shortName} — ${selectedYearOption.label}${semesterNumber ? ` · Semester ${semesterNumber}` : ""} · ${academicYear?.label || ""}`
                            : academicYear?.label || "Select a course below"}
                    </p>
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button className="btn btn-outline" onClick={() => navigate("/timetables")}>
                        <FiX size={14} /> Cancel
                    </button>
                    <button className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
                        <FiSave size={14} /> {loading ? "Saving…" : isEdit ? "Update Timetable" : "Save Timetable"}
                    </button>
                </div>
            </div>

            {/* ── Course / meta setup ── */}
            <div className="card" style={{ marginBottom: "1rem" }}>
                <div className="card-header">Timetable Details</div>
                <div className="card-body">
                    <div className="form-grid">
                        <div className="form-group">
                            <label className="form-label">
                                Course{" "}
                                <span style={{ color: "var(--danger)" }}>*</span>
                            </label>

                            <select
                                className="form-control"
                                value={courseId}
                                onChange={(e) => {
                                    setCourseId(e.target.value);
                                    setYearNumber("");
                                    setSemesterNumber("");
                                }}
                            >
                                <option value="">Select course…</option>

                                {courses.map((c) => (
                                    <option key={c._id} value={c._id}>
                                        {c.shortName}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                Course Year{" "}
                                <span style={{ color: "var(--danger)" }}>*</span>
                            </label>

                            <select
                                className="form-control"
                                value={yearNumber}
                                onChange={(e) => {
                                    setYearNumber(
                                        e.target.value
                                            ? Number(e.target.value)
                                            : ""
                                    );
                                    setSemesterNumber("");
                                }}
                                disabled={!selectedCourse}
                            >
                                <option value="">
                                    {selectedCourse
                                        ? "Select year…"
                                        : "Select a course first"}
                                </option>

                                {yearOptions.map((y) => (
                                    <option
                                        key={y.yearNumber}
                                        value={y.yearNumber}
                                    >
                                        {y.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Semester</label>

                            <select
                                className="form-control"
                                value={semesterNumber}
                                onChange={(e) =>
                                    setSemesterNumber(
                                        e.target.value
                                            ? Number(e.target.value)
                                            : ""
                                    )
                                }
                                disabled={!selectedCourse || semesterOptions.length === 0}
                            >
                                <option value="">
                                    {semesterOptions.length === 0
                                        ? "No semesters"
                                        : "Select semester (optional)…"}
                                </option>

                                {semesterOptions.map((s) => (
                                    <option
                                        key={s.semesterNumber}
                                        value={s.semesterNumber}
                                    >
                                        {s.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label className="form-label">Effective From</label>
                            <input
                                type="date"
                                className="form-control"
                                value={effectiveFrom}
                                onChange={(e) => setEffectiveFrom(e.target.value)}
                            />
                        </div>
                        <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                            <label className="form-label">Notes (optional)</label>
                            <textarea
                                className="form-control"
                                rows={2}
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="e.g. Valid from April 2025 — revised schedule"
                                style={{ resize: "vertical" }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Period slot configuration ── */}
            <div className="card" style={{ marginBottom: "1rem" }}>
                <div
                    className="card-header"
                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", userSelect: "none" }}
                    onClick={() => setSlotsExpanded((p) => !p)}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        {slotsExpanded ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
                        Period Time Slots ({slots.length} slots)
                    </div>
                    <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 400 }}>
                        Click to {slotsExpanded ? "collapse" : "expand"}
                    </span>
                </div>

                {slotsExpanded && (
                    <div className="card-body">
                        <p style={{ fontSize: 12.5, color: "var(--text-muted)", marginBottom: "1rem" }}>
                            Define the time slots that appear as columns in the timetable grid. These apply to all days.
                        </p>

                        {/* Header row */}
                        <div style={{ display: "grid", gridTemplateColumns: "40px 1fr 1fr 1fr 36px", gap: "0.5rem", marginBottom: "0.4rem" }}>
                            <span className="form-label">#</span>
                            <span className="form-label">Start Time *</span>
                            <span className="form-label">End Time *</span>
                            <span className="form-label">Label (optional)</span>
                            <span />
                        </div>

                        {slots.map((slot, idx) => (
                            <div key={idx} style={{ display: "grid", gridTemplateColumns: "40px 1fr 1fr 1fr 36px", gap: "0.5rem", marginBottom: "0.4rem", alignItems: "center" }}>
                                <span style={{ fontSize: 12.5, color: "var(--text-muted)", fontWeight: 600, textAlign: "center" }}>{slot.periodNo}</span>
                                <input
                                    type="time"
                                    className="form-control"
                                    value={slot.startTime}
                                    onChange={(e) => updateSlot(idx, "startTime", e.target.value)}
                                />
                                <input
                                    type="time"
                                    className="form-control"
                                    value={slot.endTime}
                                    onChange={(e) => updateSlot(idx, "endTime", e.target.value)}
                                />
                                <input
                                    className="form-control"
                                    placeholder="e.g. Lunch, Break"
                                    value={slot.label}
                                    onChange={(e) => updateSlot(idx, "label", e.target.value)}
                                />
                                <button
                                    type="button"
                                    onClick={() => removeSlot(idx)}
                                    disabled={slots.length === 1}
                                    style={{ height: 36, width: 36, display: "flex", alignItems: "center", justifyContent: "center", background: "transparent", border: "1px solid var(--border)", borderRadius: "var(--radius)", color: "#dc2626", cursor: slots.length === 1 ? "not-allowed" : "pointer", opacity: slots.length === 1 ? 0.4 : 1 }}
                                >
                                    <FiTrash2 size={12} />
                                </button>
                            </div>
                        ))}

                        <button type="button" className="btn btn-outline btn-sm" onClick={addSlot} style={{ marginTop: "0.5rem" }}>
                            <FiPlus size={12} /> Add Slot
                        </button>
                    </div>
                )}
            </div>

            {/* ── Schedule Grid ── */}
            <div className="card">
                <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>Weekly Schedule</span>
                    <div style={{ display: "flex", gap: "0.4rem" }}>
                        <span style={{ fontSize: 12, color: "var(--text-muted)", alignSelf: "center" }}>Copy day:</span>
                        <select
                            className="form-control"
                            style={{ fontSize: 12, height: 32, width: 130 }}
                            onChange={(e) => {
                                if (e.target.value) {
                                    copyDayTo(activeDay, e.target.value);
                                    e.target.value = "";
                                }
                            }}
                        >
                            <option value="">→ Copy {activeDay} to…</option>
                            {DAYS.filter((d) => d !== activeDay).map((d) => (
                                <option key={d} value={d}>{d}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Day tabs */}
                <div style={{ display: "flex", borderBottom: "1px solid var(--border)", overflowX: "auto" }}>
                    {DAYS.map((day) => {
                        const daySchedule = schedule.find((d) => d.day === day);
                        const filled = daySchedule?.periods.filter((p) => p.subjectName).length || 0;
                        const isActive = activeDay === day;
                        return (
                            <button
                                key={day}
                                type="button"
                                onClick={() => setActiveDay(day)}
                                style={{
                                    padding: "0.6rem 1rem",
                                    border: "none",
                                    borderBottom: isActive ? "3px solid #003b67" : "3px solid transparent",
                                    background: "transparent",
                                    fontSize: 13,
                                    fontWeight: isActive ? 600 : 400,
                                    color: isActive ? "#003b67" : "var(--text-muted)",
                                    cursor: "pointer",
                                    whiteSpace: "nowrap",
                                    position: "relative",
                                }}
                            >
                                {day.slice(0, 3)}
                                {filled > 0 && (
                                    <span style={{ marginLeft: 5, fontSize: 10, background: "#003b67", color: "#fff", borderRadius: 99, padding: "1px 5px" }}>
                                        {filled}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* Grid: periods as rows */}
                <div style={{ padding: "1rem", overflowX: "auto" }}>
                    {slots.length === 0 ? (
                        <div style={{ textAlign: "center", color: "var(--text-muted)", padding: "2rem", fontSize: 13 }}>
                            Add period slots above first.
                        </div>
                    ) : (
                        <div style={{ display: "grid", gap: "0.5rem" }}>
                            {slots.map((slot, slotIdx) => {
                                const period = activeDaySchedule.periods[slotIdx] || emptyCell(slot.periodNo);
                                const isEmpty = !period.subjectName;
                                const tc = TYPE_COLORS[period.type] || TYPE_COLORS.Lecture;
                                const isBreakSlot = slot.label?.toLowerCase().includes("break") || slot.label?.toLowerCase().includes("lunch");

                                return (
                                    <div
                                        key={slot.periodNo}
                                        onClick={() => openCell(activeDayIdx, slotIdx)}
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: "100px 1fr",
                                            gap: "0.75rem",
                                            alignItems: "center",
                                            padding: "0.65rem 0.85rem",
                                            borderRadius: 8,
                                            border: `1px solid ${isEmpty ? "var(--border)" : tc.color + "40"}`,
                                            background: isEmpty
                                                ? isBreakSlot ? "#f9fafb" : "#fff"
                                                : tc.bg,
                                            cursor: "pointer",
                                            transition: "box-shadow 0.12s",
                                        }}
                                        onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)"; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; }}
                                    >
                                        {/* Time */}
                                        <div style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600 }}>
                                            <div>{slot.startTime} – {slot.endTime}</div>
                                            {slot.label && (
                                                <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>{slot.label}</div>
                                            )}
                                        </div>

                                        {/* Content */}
                                        {isEmpty ? (
                                            <div style={{ fontSize: 12.5, color: "#c4c4c4", fontStyle: "italic" }}>
                                                Click to add…
                                            </div>
                                        ) : (
                                            <div>
                                                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                                    <span style={{ background: tc.bg, color: tc.color, fontSize: 10.5, fontWeight: 700, padding: "2px 7px", borderRadius: 99, border: `1px solid ${tc.color}30` }}>
                                                        {period.type}
                                                    </span>
                                                    <span style={{ fontSize: 13.5, fontWeight: 700, color: tc.color }}>
                                                        {period.subjectName}
                                                    </span>
                                                </div>
                                                <div style={{ display: "flex", gap: "1rem", fontSize: 12, color: "var(--text-muted)", marginTop: 3 }}>
                                                    {period.teacher && <span>{period.teacher}</span>}
                                                    {period.room && <span>📍 {period.room}</span>}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Legend */}
                <div style={{ padding: "0.5rem 1rem 0.75rem", display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
                    {PERIOD_TYPES.map((t) => {
                        const tc = TYPE_COLORS[t];
                        return (
                            <span key={t} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11.5, color: tc.color }}>
                                <span style={{ width: 10, height: 10, borderRadius: 3, background: tc.bg, border: `1px solid ${tc.color}50`, display: "inline-block" }} />
                                {t}
                            </span>
                        );
                    })}
                    <span style={{ fontSize: 11.5, color: "var(--text-muted)", marginLeft: "auto" }}>
                        Click any row to edit
                    </span>
                </div>
            </div>

            {/* ── Cell editor modal ── */}
            {editingCell && (() => {
                const { dayIdx, periodIdx } = editingCell;
                const cell = schedule[dayIdx]?.periods[periodIdx] || emptyCell(slots[periodIdx]?.periodNo);
                const slot = slots[periodIdx] || {};
                return (
                    <CellModal
                        cell={cell}
                        slotLabel={slot.label}
                        day={DAYS[dayIdx]}
                        subjects={masterSubjects}
                        onSave={saveCell}
                        onClose={() => setEditingCell(null)}
                    />
                );
            })()}
        </div>
    );
}
